// src/admin/pages/users/UserManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Avatar,
  Pagination,
  InputAdornment,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  AdminPanelSettings as RoleIcon,
} from '@mui/icons-material';
import { User, UserRole } from './userService';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useUsersPaginated,
  useUserStats,
  useUserMutations,
} from '../../../hooks/useUsers';
import { useAllRoles } from '../../../hooks/useRoles';
import ToastNotification from '../common/Toastnotification';

const USERS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface DeleteDialogState {
  open: boolean;
  user: User | null;
  confirmText: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Chip style helper
// ─────────────────────────────────────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.12)',  border: 'rgba(255,167,38,0.4)',  text: '#b76e00', iconColor: '#e68900' },
    info:      { bg: 'rgba(41,182,246,0.12)',  border: 'rgba(41,182,246,0.4)',  text: '#0277bd', iconColor: '#039be5' },
    primary:   { bg: 'rgba(33,150,243,0.12)',  border: 'rgba(33,150,243,0.4)',  text: '#1565c0', iconColor: '#1976d2' },
    success:   { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error:     { bg: 'rgba(239,83,80,0.12)',   border: 'rgba(239,83,80,0.4)',   text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)',  border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default:   { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.22)',  border: 'rgba(255,167,38,0.55)',  text: '#ffcc80', iconColor: '#ffa726' },
    info:      { bg: 'rgba(41,182,246,0.20)',  border: 'rgba(41,182,246,0.50)',  text: '#81d4fa', iconColor: '#29b6f6' },
    primary:   { bg: 'rgba(33,150,243,0.22)',  border: 'rgba(33,150,243,0.50)',  text: '#90caf9', iconColor: '#42a5f5' },
    success:   { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error:     { bg: 'rgba(239,83,80,0.22)',   border: 'rgba(239,83,80,0.50)',   text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)',  border: 'rgba(171,71,188,0.50)',  text: '#ce93d8', iconColor: '#ab47bc' },
    default:   { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

// Cycle through chip colours by index so each dynamic role gets a distinct colour
const ROLE_CHIP_COLORS = ['primary', 'secondary', 'info', 'warning', 'success', 'error'];
const getRoleChipColor = (index: number) => ROLE_CHIP_COLORS[index % ROLE_CHIP_COLORS.length];

// ─────────────────────────────────────────────────────────────────────────────
// LoadingOverlay
// ─────────────────────────────────────────────────────────────────────────────
interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();
  const overlayBg =
    theme.palette.mode === 'dark' ? 'rgba(26, 29, 36, 0.85)' : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        pointerEvents: 'all',
        borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading users...
        </Typography>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// UserManagement
// ─────────────────────────────────────────────────────────────────────────────
const UserManagement: React.FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Permission flags ───────────────────────────────────────────────────────
  // isSuperAdmin bypasses all checks — all actions are visible and enabled.
  // For non-super-admin users, each action is gated by the relevant permission code.
  const canView         = isSuperAdmin || hasPermission('users.view');
  const canEdit         = isSuperAdmin || hasPermission('users.edit');
  const canAssignRole   = isSuperAdmin || hasPermission('users.assign_role');
  const canToggleStatus = isSuperAdmin || hasPermission('users.toggle_status');
  const canDelete       = isSuperAdmin || hasPermission('users.delete');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<{
    roleId: string;
    roleName: string;
    isActive: boolean;
  }>({ roleId: '', roleName: '', isActive: true });
  const [saveLoading, setSaveLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  // ── Delete dialog ─────────────────────────────────────────────────────────
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    user: null,
    confirmText: '',
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── Pagination & filter ───────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // ── Data hooks ────────────────────────────────────────────────────────────
  const {
    data: usersData,
    isLoading: usersLoading,
    isFetching: usersIsFetching,
    isError: usersIsError,
    error: usersErrorObj,
  } = useUsersPaginated({
    page: currentPage,
    search: searchQuery,
    role: roleFilter,
    status: statusFilter,
    pageSize: USERS_PER_PAGE,
  });

  const { data: statsData } = useUserStats();
  const { updateUser, toggleStatus, deleteUser } = useUserMutations();

  // Fetch all roles from the roles collection for dropdown + filter
  const { data: allRoles = [], isLoading: rolesLoading } = useAllRoles();

  // ── Derived values ────────────────────────────────────────────────────────
  const users = usersData?.users ?? [];
  const totalUsers = usersData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalUsers / USERS_PER_PAGE);
  const totalActiveUsers = statsData?.activeUsers ?? 0;
  const totalAdminUsers = statsData?.adminUsers ?? 0;
  const totalInactiveUsers = totalUsers - totalActiveUsers;
  const loading = usersLoading || usersIsFetching;

  const isPermissionError =
    usersIsError &&
    ((usersErrorObj as Error)?.message?.includes('permission') ||
      (usersErrorObj as Error)?.message?.includes('not have permission'));

  // Build a lookup: roleId → { name, colorIndex } for display
  const roleColorMap = React.useMemo(() => {
    const map: Record<string, { name: string; colorIndex: number }> = {};
    allRoles.forEach((r, i) => {
      if (r.id) map[r.id] = { name: r.name, colorIndex: i };
    });
    return map;
  }, [allRoles]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: ToastState['severity']) =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  const hasActiveFilters =
    searchQuery !== '' || roleFilter !== 'all' || statusFilter !== 'all';
  const activeFiltersCount = [
    searchQuery !== '',
    roleFilter !== 'all',
    statusFilter !== 'all',
  ].filter(Boolean).length;

  const resetToFirstPage = () => setCurrentPage(1);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) =>
    setCurrentPage(page);

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
    resetToFirstPage();
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setRoleFilter('all');
    setStatusFilter('all');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  // ── Drawer ────────────────────────────────────────────────────────────────
  const handleEditUser = (user: User) => {
    if (!canEdit && !canAssignRole) return;
    setFormData({
      roleId: user.roleId || '',
      roleName: user.roleName || '',
      isActive: user.isActive !== false,
    });
    setSelectedUser(user);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedUser(null);
  };

  // ── Toggle status ─────────────────────────────────────────────────────────
  const handleToggleStatus = async (user: User) => {
    if (!canToggleStatus) return;
    try {
      const newStatus = !user.isActive;
      await toggleStatus.mutateAsync({ uid: user.uid, isActive: newStatus });
      resetToFirstPage();
      showToast(`User ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
    } catch (err: any) {
      showToast(`Failed to update user status: ${err.message || 'Unknown error'}`, 'error');
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleOpenDeleteDialog = (user: User) => {
    if (!canDelete) return;
    setDeleteDialog({ open: true, user, confirmText: '' });
  };

  const handleCloseDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialog({ open: false, user: null, confirmText: '' });
  };

  const handleDeleteConfirmTextChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setDeleteDialog((prev) => ({ ...prev, confirmText: e.target.value }));

  const isDeleteConfirmed =
    deleteDialog.confirmText.trim().toLowerCase() ===
    deleteDialog.user?.email?.trim().toLowerCase();

  const handleDeleteUser = async () => {
    if (!deleteDialog.user || !isDeleteConfirmed) return;
    try {
      setDeleteLoading(true);
      await deleteUser.mutateAsync({ uid: deleteDialog.user.uid });
      setDeleteDialog({ open: false, user: null, confirmText: '' });
      resetToFirstPage();
      showToast('User deleted successfully', 'success');
    } catch (err: any) {
      showToast(`Failed to delete user: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Form submit ───────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    if (!formData.roleId) {
      showToast('Please select a role', 'warning');
      return;
    }
    try {
      setSaveLoading(true);
      await updateUser.mutateAsync({
        uid: selectedUser.uid,
        roleId: formData.roleId,
        roleName: formData.roleName,
        isActive: formData.isActive,
      });
      resetToFirstPage();
      setDrawerOpen(false);
      setSelectedUser(null);
      showToast('User updated successfully', 'success');
    } catch (err: any) {
      showToast(`Failed to update user: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleRoleSelectChange = (roleId: string) => {
    const found = allRoles.find((r) => r.id === roleId);
    setFormData((prev) => ({
      ...prev,
      roleId,
      roleName: found?.name || '',
    }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData((prev) => ({ ...prev, isActive: e.target.checked }));

  // ── Role chip for table rows ──────────────────────────────────────────────
  const renderRoleChip = (user: User) => {
    if (user.roleId && roleColorMap[user.roleId]) {
      const { name, colorIndex } = roleColorMap[user.roleId];
      return (
        <Chip
          icon={<RoleIcon fontSize="small" />}
          label={name}
          size="small"
          variant="outlined"
          sx={getSoftChipSx(getRoleChipColor(colorIndex), isDark)}
        />
      );
    }
    return (
      <Chip
        icon={<PersonIcon fontSize="small" />}
        label="Unassigned"
        size="small"
        variant="outlined"
        sx={getSoftChipSx('default', isDark)}
      />
    );
  };

  // ── Access denied — no users.view permission ──────────────────────────────
  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <PeopleIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>
              Access Denied
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
            >
              You don't have permission to view users. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Initial loading ───────────────────────────────────────────────────────
  if (usersLoading && users.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading users...
        </Typography>
      </Box>
    );
  }

  if (isPermissionError) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <PeopleIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>
              Access Denied
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
            >
              You don't have permission to view users. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <PeopleIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}
            >
              User Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage user roles and access permissions
            </Typography>
          </Box>
        </Stack>

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Total Users
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {totalUsers}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" color="success.main" noWrap>
                      {totalActiveUsers} Active
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'text.disabled',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {totalInactiveUsers} Inactive
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Active Users
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {totalActiveUsers}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalUsers > 0
                      ? `${((totalActiveUsers / totalUsers) * 100).toFixed(1)}% of total`
                      : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Super Admins
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {totalAdminUsers}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalUsers > 0
                      ? `${((totalAdminUsers / totalUsers) * 100).toFixed(1)}% of total`
                      : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Inactive Users
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {totalInactiveUsers}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalUsers > 0
                      ? `${((totalInactiveUsers / totalUsers) * 100).toFixed(1)}% suspended`
                      : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search users... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button
                  onClick={clearFilters}
                  variant="text"
                  size="small"
                  startIcon={<ClearIcon />}
                >
                  Clear
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                {/* Dynamic role filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={roleFilter}
                      onChange={(e) => handleRoleFilterChange(e.target.value)}
                      label="Role"
                      disabled={rolesLoading}
                    >
                      <MenuItem value="all">All Roles</MenuItem>
                      {allRoles.map((role) => (
                        <MenuItem key={role.id} value={role.id!}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active Only</MenuItem>
                      <MenuItem value="inactive">Inactive Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${users.length} users matching your filters`
                        : `Showing ${users.length} users on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {users.length === 0 && !loading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
            }}
          >
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <PeopleIcon
                  sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }}
                />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Users Match Your Filters' : 'No Users Found'}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
                >
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No users are registered in the system yet.'}
                </Typography>
                {hasActiveFilters && (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {users.length > 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 'auto', md: 0 },
            }}
          >
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 480, md: 800 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      Email
                    </TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      Joined
                    </TableCell>
                    {/* Only show Actions column if the user has at least one action permission */}
                    {(canEdit || canAssignRole || canToggleStatus || canDelete) && (
                      <TableCell align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user.uid}
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      {/* User */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar
                            src={user.photoURL || undefined}
                            alt={user.displayName || user.email}
                            sx={{
                              width: { xs: 32, sm: 40 },
                              height: { xs: 32, sm: 40 },
                              flexShrink: 0,
                            }}
                          >
                            {(user.displayName || user.email || 'U')[0].toUpperCase()}
                          </Avatar>
                          <Box minWidth={0}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              }}
                              noWrap
                            >
                              {user.displayName || 'No Name'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: { xs: 'block', sm: 'none' } }}
                              noWrap
                            >
                              {user.email}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: { xs: 'none', sm: 'block' } }}
                            >
                              UID: {user.uid.substring(0, 8)}...
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Email */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {user.email}
                        </Typography>
                      </TableCell>

                      {/* Role chip — dynamic */}
                      <TableCell>{renderRoleChip(user)}</TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={user.isActive !== false ? 'Active' : 'Inactive'}
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx(
                            user.isActive !== false ? 'success' : 'default',
                            isDark
                          )}
                        />
                      </TableCell>

                      {/* Joined */}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString()
                            : '-'}
                        </Typography>
                      </TableCell>

                      {/* Actions — each button shown only when the user has the relevant permission */}
                      {(canEdit || canAssignRole || canToggleStatus || canDelete) && (
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            flexWrap="nowrap"
                          >
                            {/* Edit / Assign Role */}
                            {(canEdit || canAssignRole) && (
                              <Tooltip title="Edit User">
                                <span>
                                  <IconButton
                                    onClick={() => handleEditUser(user)}
                                    size="small"
                                    color="warning"
                                    disabled={user.uid === currentUser?.uid}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {/* Toggle active status */}
                            {canToggleStatus && (
                              <Tooltip
                                title={
                                  user.isActive !== false ? 'Deactivate' : 'Activate'
                                }
                              >
                                <span>
                                  <IconButton
                                    onClick={() => handleToggleStatus(user)}
                                    size="small"
                                    color="info"
                                    disabled={user.uid === currentUser?.uid}
                                  >
                                    {user.isActive !== false ? (
                                      <VisibilityOffIcon fontSize="small" />
                                    ) : (
                                      <VisibilityIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {/* Delete */}
                            {canDelete && (
                              <Tooltip title="Delete User">
                                <span>
                                  <IconButton
                                    onClick={() => handleOpenDeleteDialog(user)}
                                    size="small"
                                    color="error"
                                    disabled={user.uid === currentUser?.uid}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            {/* Pagination */}
            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Showing{' '}
                    {totalUsers === 0 ? 0 : (currentPage - 1) * USERS_PER_PAGE + 1}–
                    {Math.min(currentPage * USERS_PER_PAGE, totalUsers)} of {totalUsers} users
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Edit Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 440 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <AdminIcon color="primary" />
              <Typography variant="h6">Edit User</Typography>
            </Stack>
            <IconButton onClick={handleCloseDrawer} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                px: 3,
                py: 3,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
              }}
            >
              {selectedUser && (
                <Stack spacing={3}>
                  {/* User summary */}
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      src={selectedUser.photoURL || undefined}
                      sx={{ width: 56, height: 56, flexShrink: 0 }}
                    >
                      {(selectedUser.displayName || selectedUser.email || 'U')[0].toUpperCase()}
                    </Avatar>
                    <Box minWidth={0}>
                      <Typography variant="h6" noWrap>
                        {selectedUser.displayName || 'No Name'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {selectedUser.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        UID: {selectedUser.uid}
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider />

                  {/* Dynamic Role selector — shown only when user can assign roles */}
                  {(canEdit || canAssignRole) && (
                    <FormControl fullWidth required>
                      <InputLabel>Assign Role</InputLabel>
                      <Select
                        value={formData.roleId}
                        onChange={(e) => handleRoleSelectChange(e.target.value)}
                        label="Assign Role"
                        disabled={rolesLoading}
                        renderValue={(selected) => {
                          if (!selected) return <em>Select a role…</em>;
                          const found = allRoles.find((r) => r.id === selected);
                          return found ? found.name : selected;
                        }}
                      >
                        {rolesLoading ? (
                          <MenuItem disabled>
                            <CircularProgress size={16} sx={{ mr: 1 }} /> Loading roles…
                          </MenuItem>
                        ) : allRoles.length === 0 ? (
                          <MenuItem disabled>No roles defined yet</MenuItem>
                        ) : (
                          allRoles.map((role, i) => (
                            <MenuItem key={role.id} value={role.id!}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                sx={{ width: '100%' }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    bgcolor: `${ROLE_CHIP_COLORS[i % ROLE_CHIP_COLORS.length]}.main`,
                                  }}
                                />
                                <Box minWidth={0} flex={1}>
                                  <Typography variant="body2" fontWeight={600} noWrap>
                                    {role.name}
                                  </Typography>
                                  {role.description && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                      display="block"
                                    >
                                      {role.description}
                                    </Typography>
                                  )}
                                </Box>
                                <Chip
                                  label={`${role.permissions.length} perms`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              </Stack>
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  )}

                  {/* Selected role permissions summary */}
                  {(canEdit || canAssignRole) &&
                    formData.roleId &&
                    (() => {
                      const selectedRole = allRoles.find((r) => r.id === formData.roleId);
                      if (!selectedRole) return null;
                      return (
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: isDark
                              ? 'rgba(255,255,255,0.04)'
                              : 'rgba(0,0,0,0.03)',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            display="block"
                            sx={{ mb: 0.75 }}
                          >
                            {selectedRole.name} — {selectedRole.permissions.length} permissions
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedRole.description || 'No description provided.'}
                          </Typography>
                        </Box>
                      );
                    })()}

                  {/* Active status toggle — shown only when user can toggle status */}
                  {canToggleStatus && (
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isActive}
                            onChange={handleSwitchChange}
                          />
                        }
                        label="Active Status"
                      />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Inactive users cannot access the system
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>

            <Divider />
            <Box
              sx={{
                px: 3,
                py: 2,
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-end',
                flexShrink: 0,
              }}
            >
              <Button
                onClick={handleCloseDrawer}
                disabled={saveLoading}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saveLoading || !formData.roleId}
                startIcon={
                  saveLoading ? <CircularProgress size={16} /> : <SaveIcon />
                }
              >
                Update User
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, border: `1px solid ${theme.palette.error.main}33` },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'error.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <WarningIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Delete User
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This action is permanent and cannot be undone
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5 }}>
          {deleteDialog.user && (
            <Stack spacing={2.5}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: isDark
                    ? 'rgba(239,83,80,0.08)'
                    : 'rgba(239,83,80,0.05)',
                  border: `1px solid ${theme.palette.error.main}33`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Avatar
                  src={deleteDialog.user.photoURL || undefined}
                  sx={{ width: 48, height: 48, flexShrink: 0 }}
                >
                  {(
                    deleteDialog.user.displayName ||
                    deleteDialog.user.email ||
                    'U'
                  )[0].toUpperCase()}
                </Avatar>
                <Box minWidth={0}>
                  <Typography variant="subtitle1" fontWeight={600} noWrap>
                    {deleteDialog.user.displayName || 'No Name'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {deleteDialog.user.email}
                  </Typography>
                  <Stack direction="row" spacing={0.75} mt={0.5} flexWrap="wrap">
                    {deleteDialog.user.roleId &&
                    roleColorMap[deleteDialog.user.roleId] ? (
                      <Chip
                        label={roleColorMap[deleteDialog.user.roleId].name}
                        size="small"
                        variant="outlined"
                        sx={getSoftChipSx(
                          getRoleChipColor(
                            roleColorMap[deleteDialog.user.roleId].colorIndex
                          ),
                          isDark
                        )}
                      />
                    ) : (
                      <Chip
                        label="Unassigned"
                        size="small"
                        variant="outlined"
                        sx={getSoftChipSx('default', isDark)}
                      />
                    )}
                    <Chip
                      label={
                        deleteDialog.user.isActive !== false ? 'Active' : 'Inactive'
                      }
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(
                        deleteDialog.user.isActive !== false ? 'success' : 'default',
                        isDark
                      )}
                    />
                  </Stack>
                </Box>
              </Box>

              <Alert severity="error" icon={<DeleteIcon fontSize="small" />}>
                Deleting this user will permanently remove their profile from Firestore.
                Their Firebase Authentication account will remain unless removed separately
                via the Admin SDK.
              </Alert>

              <Box>
                <DialogContentText sx={{ mb: 1, fontSize: '0.9rem' }}>
                  To confirm, type the user's email address below:
                </DialogContentText>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.5,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'grey.100',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    color: 'error.main',
                    wordBreak: 'break-all',
                  }}
                >
                  {deleteDialog.user.email}
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  placeholder={`Type "${deleteDialog.user.email}" to confirm`}
                  value={deleteDialog.confirmText}
                  onChange={handleDeleteConfirmTextChange}
                  disabled={deleteLoading}
                  error={deleteDialog.confirmText.length > 0 && !isDeleteConfirmed}
                  helperText={
                    deleteDialog.confirmText.length > 0 && !isDeleteConfirmed
                      ? 'Email does not match'
                      : ' '
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: isDeleteConfirmed
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                      },
                    },
                  }}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={deleteLoading}
            variant="outlined"
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            disabled={!isDeleteConfirmed || deleteLoading}
            variant="contained"
            color="error"
            startIcon={
              deleteLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteLoading ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default UserManagement;


// // src/admin/pages/users/UserManagement.tsx
// import React, { useState, useRef } from 'react';
// import {
//   Box,
//   Button,
//   Card,
//   CardContent,
//   Drawer,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogContentText,
//   DialogActions,
//   Divider,
//   FormControl,
//   FormControlLabel,
//   Grid,
//   IconButton,
//   InputLabel,
//   MenuItem,
//   Paper,
//   Select,
//   Switch,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   TextField,
//   Typography,
//   Alert,
//   CircularProgress,
//   Stack,
//   Chip,
//   Avatar,
//   Pagination,
//   InputAdornment,
//   Collapse,
//   Tooltip,
//   useTheme,
//   useMediaQuery,
// } from '@mui/material';
// import {
//   Edit as EditIcon,
//   Delete as DeleteIcon,
//   People as PeopleIcon,
//   Visibility as VisibilityIcon,
//   VisibilityOff as VisibilityOffIcon,
//   Save as SaveIcon,
//   Cancel as CancelIcon,
//   AdminPanelSettings as AdminIcon,
//   Person as PersonIcon,
//   Search as SearchIcon,
//   FilterList as FilterListIcon,
//   Clear as ClearIcon,
//   ExpandMore as ExpandMoreIcon,
//   ExpandLess as ExpandLessIcon,
//   Close as CloseIcon,
//   Warning as WarningIcon,
//   AdminPanelSettings as RoleIcon,
// } from '@mui/icons-material';
// import { User, UserRole } from './userService';
// import { useAuth } from '../../../contexts/AuthContext';
// import {
//   useUsersPaginated,
//   useUserStats,
//   useUserMutations,
// } from '../../../hooks/useUsers';
// import { useAllRoles } from '../../../hooks/useRoles';
// import ToastNotification from '../common/Toastnotification';

// const USERS_PER_PAGE = 10;

// interface ToastState {
//   open: boolean;
//   message: string;
//   severity: 'success' | 'error' | 'warning' | 'info';
// }

// interface DeleteDialogState {
//   open: boolean;
//   user: User | null;
//   confirmText: string;
// }

// // ─────────────────────────────────────────────────────────────────────────────
// // Chip style helper
// // ─────────────────────────────────────────────────────────────────────────────
// const getSoftChipSx = (color: string, isDark: boolean = false) => {
//   type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

//   const lightPalette: Record<string, PaletteEntry> = {
//     warning:   { bg: 'rgba(255,167,38,0.12)',  border: 'rgba(255,167,38,0.4)',  text: '#b76e00', iconColor: '#e68900' },
//     info:      { bg: 'rgba(41,182,246,0.12)',  border: 'rgba(41,182,246,0.4)',  text: '#0277bd', iconColor: '#039be5' },
//     primary:   { bg: 'rgba(33,150,243,0.12)',  border: 'rgba(33,150,243,0.4)',  text: '#1565c0', iconColor: '#1976d2' },
//     success:   { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)',text: '#2e7d32', iconColor: '#43a047' },
//     error:     { bg: 'rgba(239,83,80,0.12)',   border: 'rgba(239,83,80,0.4)',   text: '#c62828', iconColor: '#e53935' },
//     secondary: { bg: 'rgba(171,71,188,0.10)',  border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
//     default:   { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
//   };

//   const darkPalette: Record<string, PaletteEntry> = {
//     warning:   { bg: 'rgba(255,167,38,0.22)',  border: 'rgba(255,167,38,0.55)',  text: '#ffcc80', iconColor: '#ffa726' },
//     info:      { bg: 'rgba(41,182,246,0.20)',  border: 'rgba(41,182,246,0.50)',  text: '#81d4fa', iconColor: '#29b6f6' },
//     primary:   { bg: 'rgba(33,150,243,0.22)',  border: 'rgba(33,150,243,0.50)',  text: '#90caf9', iconColor: '#42a5f5' },
//     success:   { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
//     error:     { bg: 'rgba(239,83,80,0.22)',   border: 'rgba(239,83,80,0.50)',   text: '#ef9a9a', iconColor: '#ef5350' },
//     secondary: { bg: 'rgba(171,71,188,0.22)',  border: 'rgba(171,71,188,0.50)',  text: '#ce93d8', iconColor: '#ab47bc' },
//     default:   { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
//   };

//   const palette = isDark ? darkPalette : lightPalette;
//   const p = palette[color] ?? palette.default;

//   return {
//     backgroundColor: p.bg,
//     borderColor: p.border,
//     color: p.text,
//     fontWeight: 600,
//     border: `1px solid ${p.border}`,
//     '& .MuiChip-icon': { color: p.iconColor },
//     '& .MuiChip-label': { color: p.text },
//   };
// };

// // Cycle through chip colours by index so each dynamic role gets a distinct colour
// const ROLE_CHIP_COLORS = ['primary', 'secondary', 'info', 'warning', 'success', 'error'];
// const getRoleChipColor = (index: number) => ROLE_CHIP_COLORS[index % ROLE_CHIP_COLORS.length];

// // ─────────────────────────────────────────────────────────────────────────────
// // LoadingOverlay
// // ─────────────────────────────────────────────────────────────────────────────
// interface LoadingOverlayProps {
//   wrapperRef: React.RefObject<HTMLDivElement | null>;
// }

// const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
//   const [rect, setRect] = useState<DOMRect | null>(null);
//   const theme = useTheme();
//   const overlayBg =
//     theme.palette.mode === 'dark' ? 'rgba(26, 29, 36, 0.85)' : 'rgba(255, 255, 255, 0.85)';

//   React.useEffect(() => {
//     const measure = () => {
//       if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
//     };
//     measure();
//     const el = wrapperRef.current;
//     window.addEventListener('resize', measure);
//     el?.addEventListener('scroll', measure);
//     return () => {
//       window.removeEventListener('resize', measure);
//       el?.removeEventListener('scroll', measure);
//     };
//   }, [wrapperRef]);

//   if (!rect) return null;

//   return (
//     <Box
//       sx={{
//         position: 'fixed',
//         top: rect.top, left: rect.left,
//         width: rect.width, height: rect.height,
//         backgroundColor: overlayBg,
//         backdropFilter: 'blur(2px)',
//         display: 'flex', alignItems: 'center', justifyContent: 'center',
//         zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
//       }}
//     >
//       <Box sx={{ textAlign: 'center' }}>
//         <CircularProgress size={40} />
//         <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
//           Loading users...
//         </Typography>
//       </Box>
//     </Box>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // UserManagement
// // ─────────────────────────────────────────────────────────────────────────────
// const UserManagement: React.FC = () => {
//   const { currentUser } = useAuth();
//   const theme = useTheme();
//   const isDark = theme.palette.mode === 'dark';
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

//   // ── UI state ──────────────────────────────────────────────────────────────
//   const [drawerOpen, setDrawerOpen] = useState(false);
//   const [selectedUser, setSelectedUser] = useState<User | null>(null);
//   const [formData, setFormData] = useState<{
//     roleId: string;
//     roleName: string;
//     isActive: boolean;
//   }>({ roleId: '', roleName: '', isActive: true });
//   const [saveLoading, setSaveLoading] = useState(false);
//   const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

//   // ── Delete dialog ─────────────────────────────────────────────────────────
//   const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
//     open: false,
//     user: null,
//     confirmText: '',
//   });
//   const [deleteLoading, setDeleteLoading] = useState(false);

//   // ── Pagination & filter ───────────────────────────────────────────────────
//   const [currentPage, setCurrentPage] = useState(1);
//   const [searchInput, setSearchInput] = useState('');
//   const [searchQuery, setSearchQuery] = useState('');
//   const [roleFilter, setRoleFilter] = useState<string>('all');
//   const [statusFilter, setStatusFilter] = useState<string>('all');
//   const [showFilters, setShowFilters] = useState(false);

//   const tableWrapperRef = useRef<HTMLDivElement>(null);

//   // ── Data hooks ────────────────────────────────────────────────────────────
//   const {
//     data: usersData,
//     isLoading: usersLoading,
//     isFetching: usersIsFetching,
//     isError: usersIsError,
//     error: usersErrorObj,
//   } = useUsersPaginated({
//     page: currentPage,
//     search: searchQuery,
//     role: roleFilter,
//     status: statusFilter,
//     pageSize: USERS_PER_PAGE,
//   });

//   const { data: statsData } = useUserStats();
//   const { updateUser, toggleStatus, deleteUser } = useUserMutations();

//   // Fetch all roles from the roles collection for dropdown + filter
//   const { data: allRoles = [], isLoading: rolesLoading } = useAllRoles();

//   // ── Derived values ────────────────────────────────────────────────────────
//   const users = usersData?.users ?? [];
//   const totalUsers = usersData?.totalCount ?? 0;
//   const totalPages = Math.ceil(totalUsers / USERS_PER_PAGE);
//   const totalActiveUsers = statsData?.activeUsers ?? 0;
//   const totalAdminUsers = statsData?.adminUsers ?? 0;
//   const totalInactiveUsers = totalUsers - totalActiveUsers;
//   const loading = usersLoading || usersIsFetching;

//   const isPermissionError =
//     usersIsError &&
//     ((usersErrorObj as Error)?.message?.includes('permission') ||
//       (usersErrorObj as Error)?.message?.includes('not have permission'));

//   // Build a lookup: roleId → { name, colorIndex } for display
//   const roleColorMap = React.useMemo(() => {
//     const map: Record<string, { name: string; colorIndex: number }> = {};
//     allRoles.forEach((r, i) => {
//       if (r.id) map[r.id] = { name: r.name, colorIndex: i };
//     });
//     return map;
//   }, [allRoles]);

//   // ── Helpers ───────────────────────────────────────────────────────────────
//   const showToast = (message: string, severity: ToastState['severity']) =>
//     setToast({ open: true, message, severity });
//   const handleCloseToast = () => setToast(t => ({ ...t, open: false }));

//   const hasActiveFilters =
//     searchQuery !== '' || roleFilter !== 'all' || statusFilter !== 'all';
//   const activeFiltersCount = [
//     searchQuery !== '',
//     roleFilter !== 'all',
//     statusFilter !== 'all',
//   ].filter(Boolean).length;

//   const resetToFirstPage = () => setCurrentPage(1);

//   const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) =>
//     setCurrentPage(page);

//   const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
//     if (e.key === 'Enter') {
//       e.preventDefault();
//       setSearchQuery(searchInput);
//       resetToFirstPage();
//     }
//   };

//   const handleRoleFilterChange = (value: string) => {
//     setRoleFilter(value);
//     resetToFirstPage();
//   };

//   const handleStatusFilterChange = (value: string) => {
//     setStatusFilter(value);
//     resetToFirstPage();
//   };

//   const clearFilters = () => {
//     setSearchInput('');
//     setSearchQuery('');
//     setRoleFilter('all');
//     setStatusFilter('all');
//     resetToFirstPage();
//     showToast('Filters cleared', 'info');
//   };

//   // ── Drawer ────────────────────────────────────────────────────────────────
//   const handleEditUser = (user: User) => {
//     setFormData({
//       roleId: user.roleId || '',
//       roleName: user.roleName || '',
//       isActive: user.isActive !== false,
//     });
//     setSelectedUser(user);
//     setDrawerOpen(true);
//   };

//   const handleCloseDrawer = () => {
//     setDrawerOpen(false);
//     setSelectedUser(null);
//   };

//   // ── Toggle status ─────────────────────────────────────────────────────────
//   const handleToggleStatus = async (user: User) => {
//     try {
//       const newStatus = !user.isActive;
//       await toggleStatus.mutateAsync({ uid: user.uid, isActive: newStatus });
//       resetToFirstPage();
//       showToast(`User ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
//     } catch (err: any) {
//       showToast(`Failed to update user status: ${err.message || 'Unknown error'}`, 'error');
//     }
//   };

//   // ── Delete ────────────────────────────────────────────────────────────────
//   const handleOpenDeleteDialog = (user: User) =>
//     setDeleteDialog({ open: true, user, confirmText: '' });

//   const handleCloseDeleteDialog = () => {
//     if (deleteLoading) return;
//     setDeleteDialog({ open: false, user: null, confirmText: '' });
//   };

//   const handleDeleteConfirmTextChange = (e: React.ChangeEvent<HTMLInputElement>) =>
//     setDeleteDialog(prev => ({ ...prev, confirmText: e.target.value }));

//   const isDeleteConfirmed =
//     deleteDialog.confirmText.trim().toLowerCase() ===
//     deleteDialog.user?.email?.trim().toLowerCase();

//   const handleDeleteUser = async () => {
//     if (!deleteDialog.user || !isDeleteConfirmed) return;
//     try {
//       setDeleteLoading(true);
//       await deleteUser.mutateAsync({ uid: deleteDialog.user.uid });
//       setDeleteDialog({ open: false, user: null, confirmText: '' });
//       resetToFirstPage();
//       showToast('User deleted successfully', 'success');
//     } catch (err: any) {
//       showToast(`Failed to delete user: ${err.message || 'Unknown error'}`, 'error');
//     } finally {
//       setDeleteLoading(false);
//     }
//   };

//   // ── Form submit ───────────────────────────────────────────────────────────
//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!selectedUser) return;
//     if (!formData.roleId) {
//       showToast('Please select a role', 'warning');
//       return;
//     }
//     try {
//       setSaveLoading(true);
//       await updateUser.mutateAsync({
//         uid: selectedUser.uid,
//         roleId: formData.roleId,
//         roleName: formData.roleName,
//         isActive: formData.isActive,
//       });
//       resetToFirstPage();
//       setDrawerOpen(false);
//       setSelectedUser(null);
//       showToast('User updated successfully', 'success');
//     } catch (err: any) {
//       showToast(`Failed to update user: ${err.message || 'Unknown error'}`, 'error');
//     } finally {
//       setSaveLoading(false);
//     }
//   };

//   const handleRoleSelectChange = (roleId: string) => {
//     const found = allRoles.find(r => r.id === roleId);
//     setFormData(prev => ({
//       ...prev,
//       roleId,
//       roleName: found?.name || '',
//     }));
//   };

//   const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
//     setFormData(prev => ({ ...prev, isActive: e.target.checked }));

//   // ── Role chip for table rows ───────────────────────────────────────────────
//   const renderRoleChip = (user: User) => {
//     if (user.roleId && roleColorMap[user.roleId]) {
//       const { name, colorIndex } = roleColorMap[user.roleId];
//       return (
//         <Chip
//           icon={<RoleIcon fontSize="small" />}
//           label={name}
//           size="small"
//           variant="outlined"
//           sx={getSoftChipSx(getRoleChipColor(colorIndex), isDark)}
//         />
//       );
//     }
//     // Fallback for users with no roleId (legacy or unassigned)
//     return (
//       <Chip
//         icon={<PersonIcon fontSize="small" />}
//         label="Unassigned"
//         size="small"
//         variant="outlined"
//         sx={getSoftChipSx('default', isDark)}
//       />
//     );
//   };

//   // ── Initial loading ───────────────────────────────────────────────────────
//   if (usersLoading && users.length === 0) {
//     return (
//       <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
//         <CircularProgress size={40} />
//         <Typography variant="body1" sx={{ ml: 2 }}>
//           Loading users...
//         </Typography>
//       </Box>
//     );
//   }

//   if (isPermissionError) {
//     return (
//       <Box sx={{ p: 3 }}>
//         <Card>
//           <CardContent sx={{ textAlign: 'center', py: 6 }}>
//             <PeopleIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
//             <Typography variant="h5" gutterBottom>
//               Access Denied.
//             </Typography>
//             <Typography
//               variant="body1"
//               color="text.secondary"
//               sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
//             >
//               You don't have permission to view users. Please contact admin. Thank you.
//             </Typography>
//           </CardContent>
//         </Card>
//       </Box>
//     );
//   }

//   return (
//     <Box
//       sx={{
//         height: { md: '100%' },
//         display: 'flex',
//         flexDirection: 'column',
//         overflow: { xs: 'visible', md: 'hidden' },
//         p: { xs: 2, sm: 3 },
//       }}
//     >
//       {/* ── Fixed Header ── */}
//       <Box sx={{ flexShrink: 0 }}>
//         <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
//           <PeopleIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
//           <Box>
//             <Typography
//               variant="h4"
//               component="h1"
//               sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}
//             >
//               User Management
//             </Typography>
//             <Typography variant="body2" color="text.secondary">
//               Manage user roles and access permissions
//             </Typography>
//           </Box>
//         </Stack>

//         {/* ── Stats Cards ── */}
//         <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Users</Typography>
//                 <Typography variant="h5" component="div" color="text.primary"
//                   sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
//                   {totalUsers}
//                 </Typography>
//                 <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                     <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
//                     <Typography variant="caption" color="success.main" noWrap>{totalActiveUsers} Active</Typography>
//                   </Box>
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                     <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
//                     <Typography variant="caption" color="text.secondary" noWrap>{totalInactiveUsers} Inactive</Typography>
//                   </Box>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Active Users</Typography>
//                 <Typography variant="h5" component="div" color="text.primary"
//                   sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
//                   {totalActiveUsers}
//                 </Typography>
//                 <Box sx={{ mt: 'auto' }}>
//                   <Typography variant="caption" color="text.secondary" noWrap>
//                     {totalUsers > 0 ? `${((totalActiveUsers / totalUsers) * 100).toFixed(1)}% of total` : '—'}
//                   </Typography>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Administrators</Typography>
//                 <Typography variant="h5" component="div" color="text.primary"
//                   sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
//                   {totalAdminUsers}
//                 </Typography>
//                 <Box sx={{ mt: 'auto' }}>
//                   <Typography variant="caption" color="text.secondary" noWrap>
//                     {totalUsers > 0 ? `${((totalAdminUsers / totalUsers) * 100).toFixed(1)}% of total` : '—'}
//                   </Typography>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Inactive Users</Typography>
//                 <Typography variant="h5" component="div" color="text.primary"
//                   sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
//                   {totalInactiveUsers}
//                 </Typography>
//                 <Box sx={{ mt: 'auto' }}>
//                   <Typography variant="caption" color="text.secondary" noWrap>
//                     {totalUsers > 0 ? `${((totalInactiveUsers / totalUsers) * 100).toFixed(1)}% suspended` : '—'}
//                   </Typography>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {/* ── Search & Filters ── */}
//         <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
//           <Stack
//             direction={{ xs: 'column', sm: 'row' }}
//             spacing={2}
//             alignItems={{ xs: 'stretch', sm: 'center' }}
//             justifyContent="space-between"
//           >
//             <TextField
//               placeholder="Search users... (Press Enter)"
//               value={searchInput}
//               onChange={(e) => setSearchInput(e.target.value)}
//               onKeyPress={handleSearchKeyPress}
//               sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
//               size="small"
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <SearchIcon />
//                   </InputAdornment>
//                 ),
//               }}
//             />
//             <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
//               <Button
//                 onClick={() => setShowFilters(!showFilters)}
//                 variant={hasActiveFilters ? 'contained' : 'outlined'}
//                 startIcon={<FilterListIcon />}
//                 endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//                 size="small"
//               >
//                 Filters {hasActiveFilters && `(${activeFiltersCount})`}
//               </Button>
//               {hasActiveFilters && (
//                 <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
//                   Clear
//                 </Button>
//               )}
//             </Stack>
//           </Stack>

//           <Collapse in={showFilters}>
//             <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
//               <Grid container spacing={2}>
//                 {/* ── Dynamic role filter ── */}
//                 <Grid item xs={12} sm={6} md={3}>
//                   <FormControl fullWidth size="small">
//                     <InputLabel>Role</InputLabel>
//                     <Select
//                       value={roleFilter}
//                       onChange={(e) => handleRoleFilterChange(e.target.value)}
//                       label="Role"
//                       disabled={rolesLoading}
//                     >
//                       <MenuItem value="all">All Roles</MenuItem>
//                       {allRoles.map((role) => (
//                         <MenuItem key={role.id} value={role.id!}>
//                           {role.name}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </Grid>

//                 <Grid item xs={12} sm={6} md={3}>
//                   <FormControl fullWidth size="small">
//                     <InputLabel>Status</InputLabel>
//                     <Select
//                       value={statusFilter}
//                       onChange={(e) => handleStatusFilterChange(e.target.value)}
//                       label="Status"
//                     >
//                       <MenuItem value="all">All Status</MenuItem>
//                       <MenuItem value="active">Active Only</MenuItem>
//                       <MenuItem value="inactive">Inactive Only</MenuItem>
//                     </Select>
//                   </FormControl>
//                 </Grid>

//                 <Grid item xs={12} md={6}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
//                     <Typography variant="body2" color="text.secondary">
//                       {hasActiveFilters
//                         ? `Found ${users.length} users matching your filters`
//                         : `Showing ${users.length} users on this page`}
//                     </Typography>
//                   </Box>
//                 </Grid>
//               </Grid>
//             </Box>
//           </Collapse>
//         </Paper>
//       </Box>

//       {/* ── Scrollable Body ── */}
//       <Box
//         sx={{
//           flex: 1,
//           overflow: { xs: 'visible', md: 'auto' },
//           display: 'flex',
//           flexDirection: 'column',
//           minHeight: { xs: 'auto', md: 0 },
//         }}
//       >
//         {users.length === 0 && !loading && (
//           <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
//             <Card sx={{ width: '100%', maxWidth: 500 }}>
//               <CardContent sx={{ textAlign: 'center', py: 6 }}>
//                 <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
//                 <Typography variant="h5" color="text.secondary" gutterBottom>
//                   {hasActiveFilters ? 'No Users Match Your Filters' : 'No Users Found'}
//                 </Typography>
//                 <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
//                   {hasActiveFilters
//                     ? 'Try adjusting your search or filter criteria.'
//                     : 'No users are registered in the system yet.'}
//                 </Typography>
//                 {hasActiveFilters && (
//                   <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
//                     Clear Filters
//                   </Button>
//                 )}
//               </CardContent>
//             </Card>
//           </Box>
//         )}

//         {users.length > 0 && (
//           <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
//             <TableContainer
//               component={Paper}
//               ref={tableWrapperRef}
//               sx={{
//                 flex: 1,
//                 overflow: { xs: 'auto', md: 'auto' },
//                 maxHeight: { md: '100%' },
//                 mb: 2,
//                 position: 'relative',
//                 '& .MuiTable-root': { minWidth: { xs: 480, md: 800 } },
//               }}
//             >
//               <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
//                 <TableHead>
//                   <TableRow>
//                     <TableCell>User</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
//                     <TableCell>Role</TableCell>
//                     <TableCell>Status</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Joined</TableCell>
//                     <TableCell align="right">Actions</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {users.map((user) => (
//                     <TableRow
//                       key={user.uid}
//                       sx={{
//                         '&:hover': { backgroundColor: 'action.hover' },
//                         '&:last-child td, &:last-child th': { borderBottom: 0 },
//                       }}
//                     >
//                       {/* User */}
//                       <TableCell>
//                         <Stack direction="row" alignItems="center" spacing={1.5}>
//                           <Avatar
//                             src={user.photoURL || undefined}
//                             alt={user.displayName || user.email}
//                             sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, flexShrink: 0 }}
//                           >
//                             {(user.displayName || user.email || 'U')[0].toUpperCase()}
//                           </Avatar>
//                           <Box minWidth={0}>
//                             <Typography
//                               variant="subtitle2"
//                               sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
//                               noWrap
//                             >
//                               {user.displayName || 'No Name'}
//                             </Typography>
//                             <Typography
//                               variant="caption"
//                               color="text.secondary"
//                               sx={{ display: { xs: 'block', sm: 'none' } }}
//                               noWrap
//                             >
//                               {user.email}
//                             </Typography>
//                             <Typography
//                               variant="caption"
//                               color="text.secondary"
//                               sx={{ display: { xs: 'none', sm: 'block' } }}
//                             >
//                               UID: {user.uid.substring(0, 8)}...
//                             </Typography>
//                           </Box>
//                         </Stack>
//                       </TableCell>

//                       {/* Email */}
//                       <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
//                         <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
//                           {user.email}
//                         </Typography>
//                       </TableCell>

//                       {/* Role chip — dynamic */}
//                       <TableCell>{renderRoleChip(user)}</TableCell>

//                       {/* Status */}
//                       <TableCell>
//                         <Chip
//                           label={user.isActive !== false ? 'Active' : 'Inactive'}
//                           size="small"
//                           variant="outlined"
//                           sx={getSoftChipSx(user.isActive !== false ? 'success' : 'default', isDark)}
//                         />
//                       </TableCell>

//                       {/* Joined */}
//                       <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
//                         <Typography variant="body2" color="text.secondary">
//                           {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
//                         </Typography>
//                       </TableCell>

//                       {/* Actions */}
//                       <TableCell align="right">
//                         <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
//                           <Tooltip title="Edit User">
//                             <span>
//                               <IconButton
//                                 onClick={() => handleEditUser(user)}
//                                 size="small"
//                                 color="warning"
//                                 disabled={user.uid === currentUser?.uid}
//                               >
//                                 <EditIcon fontSize="small" />
//                               </IconButton>
//                             </span>
//                           </Tooltip>

//                           <Tooltip title={user.isActive !== false ? 'Deactivate' : 'Activate'}>
//                             <span>
//                               <IconButton
//                                 onClick={() => handleToggleStatus(user)}
//                                 size="small"
//                                 color="info"
//                                 disabled={user.uid === currentUser?.uid}
//                               >
//                                 {user.isActive !== false
//                                   ? <VisibilityOffIcon fontSize="small" />
//                                   : <VisibilityIcon fontSize="small" />}
//                               </IconButton>
//                             </span>
//                           </Tooltip>

//                           <Tooltip title="Delete User">
//                             <span>
//                               <IconButton
//                                 onClick={() => handleOpenDeleteDialog(user)}
//                                 size="small"
//                                 color="error"
//                                 disabled={user.uid === currentUser?.uid}
//                               >
//                                 <DeleteIcon fontSize="small" />
//                               </IconButton>
//                             </span>
//                           </Tooltip>
//                         </Stack>
//                       </TableCell>
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>

//             {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

//             {/* Pagination */}
//             <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
//               <Box sx={{ py: 2, px: 2 }}>
//                 <Stack
//                   direction={{ xs: 'column', sm: 'row' }}
//                   spacing={{ xs: 1.5, sm: 3 }}
//                   alignItems="center"
//                   justifyContent="space-between"
//                   sx={{ width: '100%' }}
//                 >
//                   <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
//                     Showing {totalUsers === 0 ? 0 : (currentPage - 1) * USERS_PER_PAGE + 1}–
//                     {Math.min(currentPage * USERS_PER_PAGE, totalUsers)} of {totalUsers} users
//                   </Typography>
//                   <Pagination
//                     count={Math.max(1, totalPages)}
//                     page={currentPage}
//                     onChange={handlePageChange}
//                     color="primary"
//                     size={isMobile ? 'small' : 'medium'}
//                     showFirstButton
//                     showLastButton
//                     disabled={loading || totalPages <= 1}
//                   />
//                   <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
//                     Page {currentPage} of {Math.max(1, totalPages)}
//                   </Typography>
//                 </Stack>
//               </Box>
//             </Paper>
//           </Box>
//         )}
//       </Box>

//       {/* ── Edit Drawer ── */}
//       <Drawer
//         anchor="right"
//         open={drawerOpen}
//         onClose={handleCloseDrawer}
//         PaperProps={{ sx: { width: { xs: '100%', sm: 440 } } }}
//       >
//         <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//           {/* Header */}
//           <Box
//             sx={{
//               px: 3, py: 2,
//               borderBottom: 1, borderColor: 'divider',
//               display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//               flexShrink: 0,
//             }}
//           >
//             <Stack direction="row" alignItems="center" spacing={1.5}>
//               <AdminIcon color="primary" />
//               <Typography variant="h6">Edit User</Typography>
//             </Stack>
//             <IconButton onClick={handleCloseDrawer} edge="end">
//               <CloseIcon />
//             </IconButton>
//           </Box>

//           <Box
//             component="form"
//             onSubmit={handleSubmit}
//             sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
//           >
//             <Box
//               sx={{
//                 flex: 1, overflow: 'auto', px: 3, py: 3,
//                 '&::-webkit-scrollbar': { width: '6px' },
//                 '&::-webkit-scrollbar-track': { background: 'transparent' },
//                 '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
//                 '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
//               }}
//             >
//               {selectedUser && (
//                 <Stack spacing={3}>
//                   {/* User summary */}
//                   <Stack direction="row" alignItems="center" spacing={2}>
//                     <Avatar
//                       src={selectedUser.photoURL || undefined}
//                       sx={{ width: 56, height: 56, flexShrink: 0 }}
//                     >
//                       {(selectedUser.displayName || selectedUser.email || 'U')[0].toUpperCase()}
//                     </Avatar>
//                     <Box minWidth={0}>
//                       <Typography variant="h6" noWrap>
//                         {selectedUser.displayName || 'No Name'}
//                       </Typography>
//                       <Typography variant="body2" color="text.secondary" noWrap>
//                         {selectedUser.email}
//                       </Typography>
//                       <Typography variant="caption" color="text.secondary">
//                         UID: {selectedUser.uid}
//                       </Typography>
//                     </Box>
//                   </Stack>

//                   <Divider />

//                   {/* ── Dynamic Role selector ── */}
//                   <FormControl fullWidth required>
//                     <InputLabel>Assign Role</InputLabel>
//                     <Select
//                       value={formData.roleId}
//                       onChange={(e) => handleRoleSelectChange(e.target.value)}
//                       label="Assign Role"
//                       disabled={rolesLoading}
//                       renderValue={(selected) => {
//                         if (!selected) return <em>Select a role…</em>;
//                         const found = allRoles.find(r => r.id === selected);
//                         return found ? found.name : selected;
//                       }}
//                     >
//                       {rolesLoading ? (
//                         <MenuItem disabled>
//                           <CircularProgress size={16} sx={{ mr: 1 }} /> Loading roles…
//                         </MenuItem>
//                       ) : allRoles.length === 0 ? (
//                         <MenuItem disabled>No roles defined yet</MenuItem>
//                       ) : (
//                         allRoles.map((role, i) => (
//                           <MenuItem key={role.id} value={role.id!}>
//                             <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
//                               <Box
//                                 sx={{
//                                   width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
//                                   bgcolor: `${ROLE_CHIP_COLORS[i % ROLE_CHIP_COLORS.length]}.main`,
//                                 }}
//                               />
//                               <Box minWidth={0} flex={1}>
//                                 <Typography variant="body2" fontWeight={600} noWrap>
//                                   {role.name}
//                                 </Typography>
//                                 {role.description && (
//                                   <Typography variant="caption" color="text.secondary" noWrap display="block">
//                                     {role.description}
//                                   </Typography>
//                                 )}
//                               </Box>
//                               <Chip
//                                 label={`${role.permissions.length} perms`}
//                                 size="small"
//                                 variant="outlined"
//                                 sx={{ fontSize: '0.65rem', height: 20 }}
//                               />
//                             </Stack>
//                           </MenuItem>
//                         ))
//                       )}
//                     </Select>
//                   </FormControl>

//                   {/* Show selected role permissions summary */}
//                   {formData.roleId && (() => {
//                     const selectedRole = allRoles.find(r => r.id === formData.roleId);
//                     if (!selectedRole) return null;
//                     return (
//                       <Box
//                         sx={{
//                           p: 1.5, borderRadius: 1.5,
//                           bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
//                           border: '1px solid', borderColor: 'divider',
//                         }}
//                       >
//                         <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.75 }}>
//                           {selectedRole.name} — {selectedRole.permissions.length} permissions
//                         </Typography>
//                         <Typography variant="caption" color="text.secondary">
//                           {selectedRole.description || 'No description provided.'}
//                         </Typography>
//                       </Box>
//                     );
//                   })()}

//                   {/* Active status */}
//                   <Box>
//                     <FormControlLabel
//                       control={
//                         <Switch checked={formData.isActive} onChange={handleSwitchChange} />
//                       }
//                       label="Active Status"
//                     />
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Inactive users cannot access the system
//                     </Typography>
//                   </Box>
//                 </Stack>
//               )}
//             </Box>

//             <Divider />
//             <Box
//               sx={{
//                 px: 3, py: 2,
//                 display: 'flex', gap: 1.5, justifyContent: 'flex-end',
//                 flexShrink: 0,
//               }}
//             >
//               <Button onClick={handleCloseDrawer} disabled={saveLoading} startIcon={<CancelIcon />}>
//                 Cancel
//               </Button>
//               <Button
//                 type="submit"
//                 variant="contained"
//                 disabled={saveLoading || !formData.roleId}
//                 startIcon={saveLoading ? <CircularProgress size={16} /> : <SaveIcon />}
//               >
//                 Update User
//               </Button>
//             </Box>
//           </Box>
//         </Box>
//       </Drawer>

//       {/* ── Delete Confirmation Dialog ── */}
//       <Dialog
//         open={deleteDialog.open}
//         onClose={handleCloseDeleteDialog}
//         maxWidth="sm"
//         fullWidth
//         PaperProps={{
//           sx: { borderRadius: 2, border: `1px solid ${theme.palette.error.main}33` },
//         }}
//       >
//         <DialogTitle sx={{ pb: 1 }}>
//           <Stack direction="row" alignItems="center" spacing={1.5}>
//             <Box
//               sx={{
//                 width: 40, height: 40, borderRadius: '50%',
//                 backgroundColor: 'error.main',
//                 display: 'flex', alignItems: 'center', justifyContent: 'center',
//                 flexShrink: 0,
//               }}
//             >
//               <WarningIcon sx={{ color: 'white', fontSize: 22 }} />
//             </Box>
//             <Box>
//               <Typography variant="h6" fontWeight={700}>Delete User</Typography>
//               <Typography variant="caption" color="text.secondary">
//                 This action is permanent and cannot be undone
//               </Typography>
//             </Box>
//           </Stack>
//         </DialogTitle>

//         <Divider />

//         <DialogContent sx={{ pt: 2.5 }}>
//           {deleteDialog.user && (
//             <Stack spacing={2.5}>
//               <Box
//                 sx={{
//                   p: 2, borderRadius: 1.5,
//                   backgroundColor: isDark ? 'rgba(239,83,80,0.08)' : 'rgba(239,83,80,0.05)',
//                   border: `1px solid ${theme.palette.error.main}33`,
//                   display: 'flex', alignItems: 'center', gap: 2,
//                 }}
//               >
//                 <Avatar
//                   src={deleteDialog.user.photoURL || undefined}
//                   sx={{ width: 48, height: 48, flexShrink: 0 }}
//                 >
//                   {(deleteDialog.user.displayName || deleteDialog.user.email || 'U')[0].toUpperCase()}
//                 </Avatar>
//                 <Box minWidth={0}>
//                   <Typography variant="subtitle1" fontWeight={600} noWrap>
//                     {deleteDialog.user.displayName || 'No Name'}
//                   </Typography>
//                   <Typography variant="body2" color="text.secondary" noWrap>
//                     {deleteDialog.user.email}
//                   </Typography>
//                   <Stack direction="row" spacing={0.75} mt={0.5} flexWrap="wrap">
//                     {deleteDialog.user.roleId && roleColorMap[deleteDialog.user.roleId] ? (
//                       <Chip
//                         label={roleColorMap[deleteDialog.user.roleId].name}
//                         size="small"
//                         variant="outlined"
//                         sx={getSoftChipSx(
//                           getRoleChipColor(roleColorMap[deleteDialog.user.roleId].colorIndex),
//                           isDark
//                         )}
//                       />
//                     ) : (
//                       <Chip
//                         label="Unassigned"
//                         size="small"
//                         variant="outlined"
//                         sx={getSoftChipSx('default', isDark)}
//                       />
//                     )}
//                     <Chip
//                       label={deleteDialog.user.isActive !== false ? 'Active' : 'Inactive'}
//                       size="small"
//                       variant="outlined"
//                       sx={getSoftChipSx(
//                         deleteDialog.user.isActive !== false ? 'success' : 'default',
//                         isDark
//                       )}
//                     />
//                   </Stack>
//                 </Box>
//               </Box>

//               <Alert severity="error" icon={<DeleteIcon fontSize="small" />}>
//                 Deleting this user will permanently remove their profile from Firestore.
//                 Their Firebase Authentication account will remain unless removed separately via the Admin SDK.
//               </Alert>

//               <Box>
//                 <DialogContentText sx={{ mb: 1, fontSize: '0.9rem' }}>
//                   To confirm, type the user's email address below:
//                 </DialogContentText>
//                 <Typography
//                   variant="body2"
//                   sx={{
//                     mb: 1.5, px: 1.5, py: 0.75, borderRadius: 1,
//                     backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'grey.100',
//                     fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.02em',
//                     color: 'error.main', wordBreak: 'break-all',
//                   }}
//                 >
//                   {deleteDialog.user.email}
//                 </Typography>
//                 <TextField
//                   autoFocus
//                   fullWidth
//                   size="small"
//                   placeholder={`Type "${deleteDialog.user.email}" to confirm`}
//                   value={deleteDialog.confirmText}
//                   onChange={handleDeleteConfirmTextChange}
//                   disabled={deleteLoading}
//                   error={deleteDialog.confirmText.length > 0 && !isDeleteConfirmed}
//                   helperText={
//                     deleteDialog.confirmText.length > 0 && !isDeleteConfirmed
//                       ? 'Email does not match'
//                       : ' '
//                   }
//                   sx={{
//                     '& .MuiOutlinedInput-root': {
//                       '&.Mui-focused fieldset': {
//                         borderColor: isDeleteConfirmed
//                           ? theme.palette.success.main
//                           : theme.palette.error.main,
//                       },
//                     },
//                   }}
//                 />
//               </Box>
//             </Stack>
//           )}
//         </DialogContent>

//         <Divider />

//         <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
//           <Button
//             onClick={handleCloseDeleteDialog}
//             disabled={deleteLoading}
//             variant="outlined"
//             startIcon={<CancelIcon />}
//           >
//             Cancel
//           </Button>
//           <Button
//             onClick={handleDeleteUser}
//             disabled={!isDeleteConfirmed || deleteLoading}
//             variant="contained"
//             color="error"
//             startIcon={
//               deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
//             }
//           >
//             {deleteLoading ? 'Deleting...' : 'Delete User'}
//           </Button>
//         </DialogActions>
//       </Dialog>

//       <ToastNotification
//         open={toast.open}
//         message={toast.message}
//         severity={toast.severity}
//         onClose={handleCloseToast}
//       />
//     </Box>
//   );
// };

// export default UserManagement;